import { useState, useEffect } from 'react';
import { X, Image, Download, Info, Check, Bot } from 'lucide-react';
import { ClassData, Category } from './types';
import { fetchAllBlueprints, BlueprintBasic } from './classBlueprintUtils';
import { fetchAllTools, ToolBasic } from './classToolUtils';

interface ClassFormProps {
  onClose: () => void;
  onSubmit: (classData: Partial<ClassData>, selectedCategories: string[], selectedBlueprints: string[], selectedTools: string[]) => void;
  categories: Category[];
  editingClass: ClassData | null;
  isAdmin?: boolean;
  initialCategoryIds?: string[];
  initialBlueprintIds?: string[];
  initialToolIds?: string[];
}

const ClassForm = ({ 
  onClose, 
  onSubmit, 
  categories,
  editingClass,
  isAdmin = false,
  initialCategoryIds = [],
  initialBlueprintIds = [],
  initialToolIds = []
}: ClassFormProps) => {
  const [classData, setClassData] = useState<Partial<ClassData>>({
    title: '',
    description: '',
    video_url: '',
  });
  const [videoProvider, setVideoProvider] = useState<'youtube' | 'streamyard'>('youtube');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [blueprints, setBlueprints] = useState<BlueprintBasic[]>([]);
  const [selectedBlueprints, setSelectedBlueprints] = useState<string[]>([]);
  const [loadingBlueprints, setLoadingBlueprints] = useState<boolean>(false);
  const [showBlueprintDetails, setShowBlueprintDetails] = useState<string | null>(null);
  const [tools, setTools] = useState<ToolBasic[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [loadingTools, setLoadingTools] = useState<boolean>(false);
  const [showToolDetails, setShowToolDetails] = useState<string | null>(null);

  useEffect(() => {
    if (editingClass) {
      setClassData({
        id: editingClass.id,
        title: editingClass.title,
        description: editingClass.description,
        video_url: editingClass.video_url,
        thumbnail: editingClass.thumbnail,
      });
      setSelectedCategories(editingClass.categories);
      // Infer provider from existing URL
      if ((editingClass.video_url || '').includes('streamyard.com')) {
        setVideoProvider('streamyard');
      } else {
        setVideoProvider('youtube');
      }
      // Preselect attachments when editing
      if (initialBlueprintIds && initialBlueprintIds.length > 0) setSelectedBlueprints(initialBlueprintIds);
      if (initialToolIds && initialToolIds.length > 0) setSelectedTools(initialToolIds);
    }
  }, [editingClass]);

  // When creating new class, seed initial selections
  useEffect(() => {
    if (!editingClass) {
      if (initialCategoryIds && initialCategoryIds.length > 0) {
        setSelectedCategories(initialCategoryIds);
      }
      if (initialBlueprintIds && initialBlueprintIds.length > 0) setSelectedBlueprints(initialBlueprintIds);
      if (initialToolIds && initialToolIds.length > 0) setSelectedTools(initialToolIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Load blueprints for admin users
  useEffect(() => {
    if (isAdmin) {
      const loadBlueprints = async () => {
        setLoadingBlueprints(true);
        try {
          const blueprintsData = await fetchAllBlueprints();
          setBlueprints(blueprintsData);
          
          // If editing, we'd fetch the associated blueprints here
          // This would need to be implemented in the backend first
          // For now, we'll just populate the selected blueprints array
          
        } catch (error) {
          console.error('Error loading blueprints:', error);
        } finally {
          setLoadingBlueprints(false);
        }
      };
      
      loadBlueprints();
    }
  }, [isAdmin, editingClass]);

  // Load tools for admin users
  useEffect(() => {
    if (isAdmin) {
      const loadTools = async () => {
        setLoadingTools(true);
        try {
          const toolsData = await fetchAllTools();
          setTools(toolsData);
        } catch (error) {
          console.error('Error loading tools:', error);
        } finally {
          setLoadingTools(false);
        }
      };
      
      loadTools();
    }
  }, [isAdmin, editingClass]);

  const handleSubmit = () => {
    if (!classData.title || !classData.description || !classData.video_url || selectedCategories.length === 0) {
      return;
    }
    
    onSubmit(classData, selectedCategories, selectedBlueprints, selectedTools);
  };

  const addMarkdownFormatting = (formatType: string) => {
    const textarea = document.getElementById('classDescription') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = classData.description || '';
    
    let newText = text;
    
    switch (formatType) {
      case 'bold':
        newText = text.substring(0, start) + '**' + text.substring(start, end) + '**' + text.substring(end);
        break;
      case 'italic':
        newText = text.substring(0, start) + '*' + text.substring(start, end) + '*' + text.substring(end);
        break;
      case 'code':
        newText = text.substring(0, start) + '`' + text.substring(start, end) + '`' + text.substring(end);
        break;
      case 'link':
        newText = text.substring(0, start) + '[' + text.substring(start, end) + '](url)' + text.substring(end);
        break;
      case 'newLine':
        newText = text.substring(0, start) + '\n\n' + text.substring(start);
        break;
    }
    
    setClassData({ ...classData, description: newText });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {editingClass ? 'Edit Class' : 'Add New Class'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <input
            type="text"
            value={classData.title || ''}
            onChange={(e) => setClassData({ ...classData, title: e.target.value })}
            placeholder="Class title"
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
          />
          <div className="flex flex-wrap gap-2 mb-2">
            <button
              type="button"
              onClick={() => addMarkdownFormatting('bold')}
              className="px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 text-sm font-medium"
            >
              Bold
            </button>
            <button
              type="button"
              onClick={() => addMarkdownFormatting('italic')}
              className="px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 text-sm font-medium"
            >
              Italic
            </button>
            <button
              type="button"
              onClick={() => addMarkdownFormatting('code')}
              className="px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 text-sm font-medium"
            >
              Code
            </button>
            <button
              type="button"
              onClick={() => addMarkdownFormatting('link')}
              className="px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 text-sm font-medium"
            >
              Link
            </button>
            <button
              type="button"
              onClick={() => addMarkdownFormatting('newLine')}
              className="px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 text-sm font-medium"
            >
              New Line
            </button>
          </div>
          <textarea
            id="classDescription"
            value={classData.description || ''}
            onChange={(e) => setClassData({ ...classData, description: e.target.value })}
            placeholder="Class description (supports markdown formatting)"
            rows={6}
            className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc] font-mono text-sm"
          />
          <p className="text-xs text-gray-400">
            Supports markdown: **bold**, *italic*, `code`, and [links](url)
          </p>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Categories (select multiple)
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategories(prev =>
                      prev.includes(category.id)
                        ? prev.filter(id => id !== category.id)
                        : [...prev, category.id]
                    );
                  }}
                  className={`px-3 py-1 rounded-full text-sm transition-colors flex items-center gap-1 ${
                    selectedCategories.includes(category.id)
                      ? 'bg-[#c9fffc] text-gray-900'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {category.img_url ? (
                    <img 
                      src={category.img_url} 
                      alt="" 
                      className="w-4 h-4 rounded-full object-cover"
                    />
                  ) : (
                    <Image className="w-3 h-3 opacity-70" />
                  )}
                  {category.name}
                </button>
              ))}
            </div>
          </div>
          {/* Video Provider */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-300 mb-1">Video Source</label>
              <select
                value={videoProvider}
                onChange={(e) => setVideoProvider(e.target.value as 'youtube' | 'streamyard')}
                className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
              >
                <option value="youtube">YouTube</option>
                <option value="streamyard">StreamYard</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Video URL</label>
              <input
                type="text"
                value={classData.video_url || ''}
                onChange={(e) => setClassData({ ...classData, video_url: e.target.value })}
                placeholder={videoProvider === 'youtube' ? 'https://www.youtube.com/watch?v=VIDEO_ID' : 'https://streamyard.com/your-recording-id'}
                {...(videoProvider === 'youtube' ? {
                  pattern: '^https:\\/\\/(www\\.)?(youtube\\.com\\/watch\\?v=|youtu\\.be\\/)[a-zA-Z0-9_-]{11}$',
                  title: 'Please enter a valid YouTube URL (e.g., https://youtube.com/watch?v=... or https://youtu.be/...)'
                } : {})}
                className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
              />
              {videoProvider === 'streamyard' && (
                <p className="text-xs text-gray-400 mt-1">
                  Paste the StreamYard recording URL (e.g., https://streamyard.com/y8j3ru9d7yiv5eif). It will be embedded as https://streamyard.com/e/&lt;id&gt;.
                </p>
              )}
            </div>
          </div>
          {/* Blueprint Selection - Only for Admin Users */}
          {isAdmin && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-300">
                  Attach Blueprints (Optional)
                </label>
                {loadingBlueprints && (
                  <span className="text-xs text-gray-400">Loading blueprints...</span>
                )}
              </div>
              
              {blueprints.length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-2 border border-gray-700 rounded-lg p-2">
                  {blueprints.map((blueprint) => (
                    <div 
                      key={blueprint.id} 
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <div 
                          className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer ${selectedBlueprints.includes(blueprint.id) ? 'bg-[#c9fffc] text-gray-900' : 'border border-gray-600'}`}
                          onClick={() => {
                            setSelectedBlueprints(prev =>
                              prev.includes(blueprint.id)
                                ? prev.filter(id => id !== blueprint.id)
                                : [...prev, blueprint.id]
                            );
                          }}
                        >
                          {selectedBlueprints.includes(blueprint.id) && <Check className="w-4 h-4" />}
                        </div>
                        <div className="flex items-center gap-2">
                          {blueprint.logo_url ? (
                            <img 
                              src={blueprint.logo_url} 
                              alt="" 
                              className="w-6 h-6 rounded object-cover" 
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center">
                              <Bot className="w-3 h-3 text-gray-400" />
                            </div>
                          )}
                          <span className="text-sm text-white">{blueprint.title}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowBlueprintDetails(showBlueprintDetails === blueprint.id ? null : blueprint.id)}
                        className="text-gray-400 hover:text-white"
                        title="View details"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                      {showBlueprintDetails === blueprint.id && (
                        <div className="absolute left-4 right-4 mt-2 p-3 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10">
                          <h4 className="text-white font-medium mb-1">{blueprint.title}</h4>
                          <p className="text-gray-300 text-sm mb-2 line-clamp-3">{blueprint.description}</p>
                          <a 
                            href={blueprint.download_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-[#c9fffc] hover:underline flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" /> View blueprint
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : !loadingBlueprints && (
                <div className="text-sm text-gray-400 p-2 border border-gray-700 rounded-lg">
                  No blueprints available to attach
                </div>
              )}
              
              <p className="text-xs text-gray-400">
                Blueprints provide students with additional learning resources
              </p>
            </div>
          )}

          {/* Tool Selection - Only for Admin Users */}
          {isAdmin && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-300">
                  Attach AI Tools (Optional)
                </label>
                {loadingTools && (
                  <span className="text-xs text-gray-400">Loading tools...</span>
                )}
              </div>
              
              {tools.length > 0 ? (
                <div className="max-h-60 overflow-y-auto space-y-2 border border-gray-700 rounded-lg p-2">
                  {tools.map((tool) => (
                    <div 
                      key={tool.id} 
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <div 
                          className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer ${selectedTools.includes(tool.id) ? 'bg-[#c9fffc] text-gray-900' : 'border border-gray-600'}`}
                          onClick={() => {
                            setSelectedTools(prev =>
                              prev.includes(tool.id)
                                ? prev.filter(id => id !== tool.id)
                                : [...prev, tool.id]
                            );
                          }}
                        >
                          {selectedTools.includes(tool.id) && <Check className="w-4 h-4" />}
                        </div>
                        <div className="flex items-center gap-2">
                          {tool.logo_url ? (
                            <img 
                              src={tool.logo_url} 
                              alt="" 
                              className="w-6 h-6 rounded object-cover" 
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center">
                              <Bot className="w-3 h-3 text-gray-400" />
                            </div>
                          )}
                          <span className="text-sm text-white">{tool.title}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowToolDetails(showToolDetails === tool.id ? null : tool.id)}
                        className="text-gray-400 hover:text-white"
                        title="View details"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                      {showToolDetails === tool.id && (
                        <div className="absolute left-4 right-4 mt-2 p-3 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10">
                          <h4 className="text-white font-medium mb-1">{tool.title}</h4>
                          <p className="text-gray-300 text-sm mb-2 line-clamp-3">{tool.description}</p>
                          <a 
                            href={tool.tool_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-[#c9fffc] hover:underline flex items-center gap-1"
                          >
                            <Bot className="w-3 h-3" /> Open tool
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : !loadingTools && (
                <div className="text-sm text-gray-400 p-2 border border-gray-700 rounded-lg">
                  No tools available to attach
                </div>
              )}
              
              <p className="text-xs text-gray-400">
                AI tools provide students with helpful utilities for their learning
              </p>
            </div>
          )}
          
          <button
            onClick={handleSubmit}
            disabled={
              !classData.title || 
              !classData.description || 
              !classData.video_url || 
              selectedCategories.length === 0
            }
            className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
          >
            {editingClass ? 'Save Changes' : 'Add Class'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassForm;
